import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import { ReportingBasisToggleContainer } from './ReportingBasisToggleContainer'

const HEADER_SELECTOR = '.Layer__UnifiedReports__BaseHeader'
const ADDITIONAL_CONTROLS_SELECTOR = '.Layer__UnifiedReports__AdditionalControls'
const CONTENT_READY_SELECTOR = '.Layer__UI__Table-ScrollContainer'
const HOST_ATTR = 'data-cash-accrual-toggle-host'

const ensureHostInside = (parent: Element): HTMLElement => {
  const existing = parent.querySelector(`:scope > [${HOST_ATTR}]`)
  if (existing instanceof HTMLElement) return existing
  const host = document.createElement('div')
  host.setAttribute(HOST_ATTR, '')
  // display:contents makes the host invisible to layout when empty,
  // so it never occupies space or affects sibling positions.
  host.style.display = 'contents'
  parent.appendChild(host)
  return host
}

const resolveHost = (header: Element): HTMLElement | null => {
  const additionalControls = header.querySelector(ADDITIONAL_CONTROLS_SELECTOR)
  if (additionalControls) {
    // Don't inject until Layer has rendered at least one of its own controls —
    // otherwise the toggle appears alone before the rest of the header mounts.
    const hasLayerControls = Array.from(additionalControls.children).some(
      c => !c.hasAttribute(HOST_ATTR)
    )
    if (!hasLayerControls) return null
    return ensureHostInside(additionalControls)
  }
  const fallback = header.lastElementChild
  if (fallback instanceof HTMLElement) return ensureHostInside(fallback)
  return null
}

export const ReportingBasisToggleInjector = () => {
  const [host, setHost] = useState<HTMLElement | null>(null)

  useEffect(() => {
    let currentHost: HTMLElement | null = null

    const resolve = () => {
      const header = document.querySelector(HEADER_SELECTOR)
      if (!header) {
        if (currentHost) { currentHost = null; setHost(null) }
        return
      }
      // Wait until report content has rendered — the table sibling goes from
      // missing to a 1-child skeleton before real rows appear (childElementCount > 1).
      const contentArea = document.querySelector(CONTENT_READY_SELECTOR)
      if (!contentArea || contentArea.childElementCount <= 1) return
      const nextHost = resolveHost(header)
      if (!nextHost || nextHost === currentHost) return
      currentHost = nextHost
      setHost(nextHost)
    }

    resolve()

    const observer = new MutationObserver(resolve)
    observer.observe(document.body, { childList: true, subtree: true })

    return () => {
      observer.disconnect()
      if (currentHost?.parentNode) currentHost.parentNode.removeChild(currentHost)
    }
  }, [])

  if (!host) return null
  return createPortal(<ReportingBasisToggleContainer />, host)
}
